import { Badge } from "@hub/components/ui/badge";
import { Button } from "@hub/components/ui/button";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@hub/components/ui/select";
import { ApiError, apiFetch } from "@hub/ui/api";
import { relativeTime } from "@hub/ui/format";
import { channelSince } from "@hub/ui/org-form";
import { ReleaseItems } from "@hub/ui/ReleaseItems";
import { useReleases } from "@hub/ui/use-releases";
import { useCallback, useEffect, useState } from "react";

interface ChannelEvent {
  action: string;
  actor: string;
  version: string;
  at: string;
  movedInstances: number;
}

interface ChannelState {
  id: "early" | "stable";
  version: string | null;
  previousVersion: string | null;
  since: string | null;
  orgs: number;
  boxes: { total: number; onVersion: number; unhealthy: number };
  events: ChannelEvent[];
}

interface MoveResult {
  moved: number;
  slugs: string[];
}

/** The Early version offered for promotion; null when Early has none or Stable already runs it. */
export function promotable(
  early: { version: string | null },
  stable: { version: string | null },
): string | null {
  if (!early.version || early.version === stable.version) return null;
  return early.version;
}

function movedLine(r: MoveResult): string {
  const noun = r.moved === 1 ? "box" : "boxes";
  return r.moved === 0 ? "Moved 0 boxes" : `Moved ${r.moved} ${noun}: ${r.slugs.join(", ")}`;
}

function ChannelFacts({ state, empty }: { state: ChannelState; empty: string }) {
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-display text-lg">{state.version ?? empty}</p>
        {state.boxes.unhealthy > 0 ? (
          <Badge variant="warning">{state.boxes.unhealthy} unhealthy</Badge>
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">{channelSince(state.since)}</p>
      <p className="text-sm">
        {state.orgs} orgs, {state.boxes.onVersion}/{state.boxes.total} boxes on it
      </p>
    </div>
  );
}

export function ChannelsPage() {
  const { releases, releasesNote } = useReleases();
  const [channels, setChannels] = useState<{ early: ChannelState; stable: ChannelState } | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [earlyVersion, setEarlyVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<"promote" | "demote" | null>(null);
  const [earlyResult, setEarlyResult] = useState<string | null>(null);
  const [earlyError, setEarlyError] = useState<string | null>(null);
  const [stableResult, setStableResult] = useState<string | null>(null);
  const [stableError, setStableError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch<{ data: { early: ChannelState; stable: ChannelState } }>(
        "/api/channels",
      );
      setChannels(r.data);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Could not load channels");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function write(
    path: string,
    body: { version: string } | null,
    report: { ok: (s: string) => void; fail: (s: string | null) => void },
  ) {
    setBusy(true);
    report.fail(null);
    try {
      const r = await apiFetch<{ data: MoveResult }>(path, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
      report.ok(movedLine(r.data));
      await load();
    } catch (e) {
      report.fail(e instanceof ApiError ? e.message : "Request failed");
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  }

  const early = channels?.early;
  const stable = channels?.stable;
  const promoteVersion = early && stable ? promotable(early, stable) : null;
  const demoteVersion = stable?.previousVersion ?? null;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Channels</h1>
      {loadError ? <p className="text-sm text-destructive">{loadError}</p> : null}
      {!channels && !loadError ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {early && stable ? (
        <div className="grid gap-6 md:grid-cols-2">
          <section className="space-y-4 rounded-md border border-border p-4">
            <h2 className="text-base font-semibold">Early</h2>
            <ChannelFacts state={early} empty="Nothing published" />
            <p className="text-sm text-muted-foreground">
              Automatic: every new release tag is published here and Nymbl's boxes follow. Choosing
              an older version below is a rollback; it holds until a newer tag is published.
            </p>
            <div className="space-y-2">
              <Select
                value={earlyVersion}
                onValueChange={(v) => {
                  if (!v) return;
                  setEarlyVersion(v);
                }}
              >
                <SelectTrigger aria-label="Release to publish to Early" className="w-full max-w-md">
                  <SelectValue placeholder="Pick a release" />
                </SelectTrigger>
                <SelectContent>
                  <ReleaseItems releases={releases} />
                </SelectContent>
              </Select>
              {releasesNote ? (
                <p className="text-xs text-muted-foreground">{releasesNote}</p>
              ) : null}
              <Button
                type="button"
                size="sm"
                disabled={busy || !earlyVersion}
                onClick={() =>
                  write(
                    "/api/channels/early",
                    { version: earlyVersion },
                    { ok: setEarlyResult, fail: setEarlyError },
                  )
                }
              >
                Publish to Early
              </Button>
              {earlyResult ? <p className="text-sm">{earlyResult}</p> : null}
              {earlyError ? <p className="text-sm text-destructive">{earlyError}</p> : null}
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-medium">Recent changes</h3>
              {early.events.length === 0 ? (
                <p className="text-sm text-muted-foreground">No changes yet.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {early.events.map((e) => (
                    <li key={`${e.at}-${e.action}-${e.version}`} className="break-words">
                      {e.action} {e.version} by {e.actor}, moved {e.movedInstances}{" "}
                      <span className="text-muted-foreground">{relativeTime(e.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="space-y-4 rounded-md border border-border p-4">
            <h2 className="text-base font-semibold">Stable</h2>
            <ChannelFacts state={stable} empty="Nothing promoted yet" />
            <p className="text-sm">Previous: {stable.previousVersion ?? "—"}</p>
            <p className="text-sm text-muted-foreground">
              Promote moves every Stable org's unpinned boxes to the version.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {confirming === "promote" && promoteVersion ? (
                <>
                  <span className="text-xs text-muted-foreground">
                    Move every Stable org's unpinned boxes to {promoteVersion}?
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      write(
                        "/api/channels/stable/promote",
                        { version: promoteVersion },
                        { ok: setStableResult, fail: setStableError },
                      )
                    }
                  >
                    {busy ? "Promoting…" : "Confirm"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirming(null)}
                  >
                    Cancel
                  </Button>
                </>
              ) : confirming === "demote" && demoteVersion ? (
                <>
                  <span className="text-xs text-muted-foreground">
                    Move every Stable org's unpinned boxes back to {demoteVersion}?
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      write("/api/channels/stable/demote", null, {
                        ok: setStableResult,
                        fail: setStableError,
                      })
                    }
                  >
                    {busy ? "Demoting…" : "Confirm"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirming(null)}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy || !promoteVersion}
                    onClick={() => setConfirming("promote")}
                  >
                    {promoteVersion ? `Promote ${promoteVersion} to Stable` : "Promote to Stable"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy || !demoteVersion}
                    onClick={() => setConfirming("demote")}
                  >
                    {demoteVersion ? `Demote to ${demoteVersion}` : "Demote"}
                  </Button>
                </>
              )}
            </div>
            {stableResult ? <p className="text-sm">{stableResult}</p> : null}
            {stableError ? <p className="text-sm text-destructive">{stableError}</p> : null}
          </section>
        </div>
      ) : null}
      <p className="text-sm text-muted-foreground">
        Every new tag is published to Early automatically within 5 minutes of its build; boxes
        follow their org's channel. A channel move never touches a pinned box or a pinned org. Nymbl
        is on Early.
      </p>
    </div>
  );
}
